import { useState, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { X, AlarmClock, Plus, Loader2, MoreVertical, Calendar, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTargetReminders } from "@/hooks/useTargetReminders";
import { ReminderFormModal } from "@/components/modals/ReminderFormModal";
import type { NotificationUpcomingItem } from "@/services/notifications.service";
import { CAPTION, HEADLINE, META, ROW_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface EntityRemindersPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  /** Target type for the notification (default: "entity") */
  targetType?: string;
  anchorTime?: Date | null;
  openDirectToForm?: boolean;
  initialEditingReminder?: NotificationUpcomingItem | null;
  reminderPayload: {
    title?: string;
    body?: string;
    deep_link?: string;
    data?: Record<string, unknown>;
  };
}

export function EntityRemindersPanel({
  open,
  onOpenChange,
  entityId,
  targetType = "entity",
  anchorTime,
  openDirectToForm = false,
  initialEditingReminder = null,
  reminderPayload,
}: EntityRemindersPanelProps) {
  const isMobile = useIsMobile();
  const { reminders, isLoading, createReminder, deleteReminder } =
    useTargetReminders({
      target: { type: targetType, id: entityId },
      payload: reminderPayload,
      enabled: open,
    });

  const [formOpen, setFormOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<NotificationUpcomingItem | null>(null);

  useEffect(() => {
    if (!open || !openDirectToForm) return;
    setEditingReminder(initialEditingReminder);
    setFormOpen(true);
    onOpenChange(false);
  }, [open, openDirectToForm, initialEditingReminder, onOpenChange]);

  const handleAddNew = useCallback(() => {
    setEditingReminder(null);
    onOpenChange(false);
    setTimeout(() => setFormOpen(true), 100);
  }, [onOpenChange]);

  const handleEdit = useCallback((reminder: NotificationUpcomingItem) => {
    setEditingReminder(reminder);
    onOpenChange(false);
    setTimeout(() => setFormOpen(true), 100);
  }, [onOpenChange]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingReminder(null);
  }, []);

  const handleFormSave = useCallback(
    async (fireAtMs: number, note?: string) => {
      if (editingReminder) {
        await deleteReminder(editingReminder.notif_id);
      }
      await createReminder(fireAtMs, note);
    },
    [editingReminder, deleteReminder, createReminder],
  );

  const handleFormSaved = useCallback(() => {
    setFormOpen(false);
    setEditingReminder(null);
  }, []);

  const listProps = {
    reminders,
    isLoading,
    onAddNew: handleAddNew,
    onEdit: handleEdit,
    onDelete: deleteReminder,
  };

  return (
    <>
      {/* List: Drawer on mobile, Dialog on desktop */}
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="max-h-[85vh]">
            <DrawerTitle className="sr-only">Reminders</DrawerTitle>
            <DrawerDescription className="sr-only">
              Manage reminders for this item
            </DrawerDescription>
            <div className="flex flex-col max-h-[70vh]">
              <ReminderListView {...listProps} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent hideClose className="max-w-md p-0 gap-0" onFocusOutside={(e) => e.preventDefault()}>
            <DialogTitle className="sr-only">Reminders</DialogTitle>
            <DialogDescription className="sr-only">
              Manage reminders for this item
            </DialogDescription>
            <div className="flex flex-col max-h-[70vh]">
              <ReminderListView {...listProps} onClose={() => onOpenChange(false)} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Form: Modal on all platforms (same pattern as AddContentModal) */}
      <ReminderFormModal
        open={formOpen}
        onOpenChange={(v) => { if (!v) handleFormClose(); }}
        anchorTime={anchorTime}
        onSave={handleFormSave}
        onSaved={handleFormSaved}
        editing={editingReminder}
      />
    </>
  );
}

/* ─── List View ─── */

function ReminderListView({
  reminders,
  isLoading,
  onAddNew,
  onEdit,
  onDelete,
  onClose,
}: {
  reminders: NotificationUpcomingItem[];
  isLoading: boolean;
  onAddNew: () => void;
  onEdit: (reminder: NotificationUpcomingItem) => void;
  onDelete: (notifId: string) => Promise<void>;
  onClose?: () => void;
}) {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-2">
        <h2 className={HEADLINE}>Reminders</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 pb-2 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : reminders.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-1.5">
            {reminders.map((r) => (
              <ReminderRow
                key={r.notif_id}
                reminder={r}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 pb-5 pt-3">
        <Button
          onClick={onAddNew}
          className="w-full"
          size="sm"
        >
          <Plus className="h-4 w-4" />
          Add Reminder
        </Button>
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
      <span className="text-display-48" role="img" aria-label="Alarm clock">
        ⏰
      </span>
      <p className={cn(META, "max-w-[220px]")}>
        Set a reminder and leave a note for when the time&apos;s right!
      </p>
    </div>
  );
}

function ReminderRow({
  reminder,
  onEdit,
  onDelete,
}: {
  reminder: NotificationUpcomingItem;
  onEdit: (reminder: NotificationUpcomingItem) => void;
  onDelete: (notifId: string) => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);

  const fireAtRaw = reminder.fire_at_ms ? Number(reminder.fire_at_ms) : NaN;
  const fireAt = Number.isFinite(fireAtRaw) ? new Date(fireAtRaw) : null;
  const dateStr = fireAt && Number.isFinite(fireAt.getTime())
    ? format(fireAt, "EEE, MMM d · h:mm a")
    : "Unknown";
  const noteText = reminder.payload?.body || null;

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(reminder.notif_id);
    setDeleting(false);
  };

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 transition-all cursor-pointer hover:bg-accent/50"
      onClick={() => !deleting && onEdit(reminder)}
      role="button"
      tabIndex={0}
    >
      <AlarmClock className="h-5 w-5 text-muted-foreground flex-shrink-0" />

      <div className="flex-1 min-w-0">
        <p className={cn(ROW_TITLE, "truncate")}>{dateStr}</p>
        {noteText ? (
          <p className={cn(CAPTION, "truncate")}>
            {noteText}
          </p>
        ) : (
          <p className={cn(CAPTION, "text-muted-foreground/50 italic")}>
            No message
          </p>
        )}
      </div>

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            disabled={deleting}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
            aria-label="Reminder options"
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MoreVertical className="h-4 w-4" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[60]" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onSelect={() => onEdit(reminder)}>
            <Calendar className="h-4 w-4 mr-2" />
            Edit Reminder
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => handleDelete()}
            disabled={deleting}
            className="text-destructive focus:text-destructive focus:bg-muted"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Cancel Reminder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

